// NodeAddUtilities.js

import { getFileNameExtension, normalizeFileMimeType } from 'utilities/fileMime';
import { stampExclusiveAncestry } from 'utilities/nodeAncestry';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';

/**
 * Applies parent linkage and copies permissions (if a parent exists).
 * Stamps ancestryPath when the child type has an exclusive structural parent.
 *
 * @param {object} child - The node to augment with parent linkage.
 * @param {object} parent - The parent node providing linkage and permissions.
 * @param {object} opts - Additional options (e.g., parent role).
 *
 * @return {Object} - The child node with applied linkage/permissions.
 */
export function applyParentLinkageAndPermissions(child, parent, opts = {}) {
    if (!parent) return child;

    const childTypeDef = opts.childTypeDef
      || getNodeType(child?.type)
      || null;

    let out = {
        ...child,
        parentTitle: parent.title || null,
        parentType: parent.type || null,
        profileRoot: false,
    };

    if (opts.role) {
        out.parentRole = opts.role;
    }

    if (parent.permissions) {
        out.permissions = deepClone(parent.permissions);
    }

    out = stampExclusiveAncestry(out, parent, childTypeDef);

    return out;
}

/**
 * Deep clones an object.
 * 
 * @param {object} obj - The object to clone.
 * 
 * @return {Object} - The deep cloned object.
 */
export function deepClone(obj) {
    return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

/**
 * Builds file metadata for a given file and node type.
 * 
 * @param {File} file - The file object.
 * @param {string} nodeTypeKey - The node type key (e.g., 'DOCUMENT', 'FILE').
 * 
 * @return {Object} - The metadata object.
 */
export function buildFileMetadata(file, nodeTypeKey) {
    if (!file?.name) return {};

    const extension = getFileNameExtension(file.name);
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    const metadata = {
        filename: file.name,
        size: file.size,
        // Normalize Chrome PDF / empty / octet-stream so downstream PDF extract works.
        mimeType: normalizeFileMimeType(file.type, file.name) || file.type || '',
        lastModified: file.lastModified || null,
        extension,
    };

    if (nodeTypeKey === 'DOCUMENT') {
        return {
            title: baseName,
            data_document: metadata,
        };
    }

    if (nodeTypeKey === 'FILE') {
        return {
            title: baseName,
            data_file: metadata,
        };
    }

    return {};
}